import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListPageLayout } from '../ui/ListPageLayout';
import { Spinner } from '../ui/Spinner';
import { CalendarFilters } from './CalendarFilters';
import { AgendaBoard } from './AgendaBoard';
import { TaskEditModal } from '../tasks/TaskEditModal';
import { AppointmentDialog } from './AppointmentDialog';
import { useCalendarEvents } from '../../hooks/queries/useCalendarEvents';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useTask, useTaskSections } from '../../hooks/queries/useTasks';
import { resolveCalendarClick, type CalendarEvent } from '../../services/calendar/calendarAggregate';
import { toast } from 'sonner';
import { useColorMap } from '../../hooks/queries/useEventTypes';
import type { AgendaColorMode } from '../../services/calendar/eventColors';
import { TaskCreateModal } from '../tasks/TaskCreateModal';
import { CalendarEventRecap } from './CalendarEventRecap';
import { CalendarQuickCreate, type QuickCreateKind, type QuickCreateSlot } from './CalendarQuickCreate';
import { shiftCursor } from '../../services/calendar/agendaLayout';
import {
  DEFAULT_DATE_FALLBACK,
  fcDateToYmd,
  parseCalendarParams,
  resolveCalendarView,
  toSearchParams,
  type CalendarUrlState,
  type CalendarView,
} from '../../services/calendar/calendarUrlState';

const CALENDAR_VIEW_KEY = 'cime.calendar.view';
const CALENDAR_COLOR_MODE_KEY = 'cime.calendar.colorMode';

function readStoredColorMode(): AgendaColorMode {
  try {
    return localStorage.getItem(CALENDAR_COLOR_MODE_KEY) === 'dossier' ? 'dossier' : 'type';
  } catch {
    return 'type';
  }
}

function readStoredCalendarView(): string | null {
  try {
    return localStorage.getItem(CALENDAR_VIEW_KEY);
  } catch {
    return null;
  }
}

function writeStoredCalendarView(view: CalendarView) {
  const stored = view === 'list' ? 'planning' : view;
  try {
    localStorage.setItem(CALENDAR_VIEW_KEY, stored);
  } catch {
    /* navigation privée : la vue reste dans l’adresse */
  }
}

export function CalendarPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const state = useMemo(() => parseCalendarParams(searchParams), [searchParams]);

  useEffect(() => {
    if (searchParams.has('view')) return;
    const view = resolveCalendarView(null, readStoredCalendarView());
    if (view === 'month') return;
    const parsed = parseCalendarParams(searchParams);
    setSearchParams(toSearchParams({ ...parsed, view }), { replace: true });
  }, [searchParams, setSearchParams]);
  const [query, setQuery] = useState('');
  const [colorMode, setColorModeState] = useState<AgendaColorMode>(readStoredColorMode);
  const colors = useColorMap();
  const setColorMode = (mode: AgendaColorMode) => {
    setColorModeState(mode);
    try {
      localStorage.setItem(CALENDAR_COLOR_MODE_KEY, mode);
    } catch {
      /* navigation privée : le choix vaut pour la page seulement */
    }
  };
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [recap, setRecap] = useState<CalendarEvent | null>(null);
  const [slot, setSlot] = useState<QuickCreateSlot | null>(null);
  const [more, setMore] = useState<QuickCreateKind | null>(null);
  const { data: editingTask } = useTask(editingTaskId);
  const { data: sections = [] } = useTaskSections(editingTask?.dossier_id ?? null);
  const { events, isLoading, isError } = useCalendarEvents(state.filters);
  const { data: collaborators } = useCollaborators();

  const today = fcDateToYmd(new Date());
  const date = state.date === DEFAULT_DATE_FALLBACK ? today : state.date;
  const lawyerNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of collaborators ?? []) map[c.id] = c.full_name;
    return map;
  }, [collaborators]);

  const appointmentId = searchParams.get('appointment');

  const update = (next: CalendarUrlState) => {
    const sp = toSearchParams(next);
    if (appointmentId) sp.set('appointment', appointmentId);
    setSearchParams(sp, { replace: true });
  };

  const closeAppointment = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('appointment');
      return sp;
    }, { replace: true });
  };

  /** Ouvre la fiche d'un objet, comme le faisait le clic direct avant la fenêtre récapitulative. */
  const openEvent = (event: CalendarEvent) => {
    const click = resolveCalendarClick(event);
    if (!click) return;
    if (click.action === 'openTask') {
      setEditingTaskId(click.taskId);
      return;
    }
    if (click.action === 'openAppointment') {
      const sp = new URLSearchParams(searchParams);
      sp.set('appointment', click.appointmentId);
      setSearchParams(sp, { replace: true });
      return;
    }
    navigate(click.href);
  };

  const setView = (view: CalendarView) => {
    writeStoredCalendarView(view);
    update({ ...state, view, date });
  };
  const setDate = (next: string) => update({ ...state, date: next, view: state.view === 'month' ? 'day' : state.view });

  return (
    <ListPageLayout className="overflow-hidden">
      <div className="flex min-w-0">
        <CalendarFilters
          filters={state.filters}
          onChange={(filters) => update({ ...state, filters, date })}
          colorMode={colorMode}
          onColorMode={setColorMode}
          colors={colors}
        />
        <div className="min-w-0 flex-1 p-4">
          {isError ? (
            <p className="py-10 text-center text-sm text-neutral-400">Agenda indisponible</p>
          ) : isLoading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : (
            <AgendaBoard
              view={state.view}
              date={date}
              today={today}
              events={events}
              lawyerNames={lawyerNames}
              query={query}
              onQuery={setQuery}
              onView={setView}
              onDate={setDate}
              onShift={(dir) => update({ ...state, date: shiftCursor(state.view, date, dir) })}
              onToday={() => update({ ...state, date: today })}
              onEvent={setRecap}
              onSlot={setSlot}
              colorMode={colorMode}
              colors={colors}
            />
          )}
        </div>
      </div>
      <TaskEditModal
        task={editingTask ?? null}
        sections={sections}
        onClose={() => setEditingTaskId(null)}
      />
      <AppointmentDialog
        open={!!appointmentId || more === 'appointment'}
        appointmentId={appointmentId}
        contextDossierId={null}
        onClose={() => {
          setMore(null);
          closeAppointment();
        }}
      />
      <TaskCreateModal open={more === 'task'} onClose={() => setMore(null)} dossierId={null} />
      <CalendarEventRecap
        event={recap}
        lawyerNames={lawyerNames}
        onClose={() => setRecap(null)}
        onOpen={(event) => {
          setRecap(null);
          openEvent(event);
        }}
        onGoToDossier={(event) => {
          setRecap(null);
          if (!event.dossierId) return;
          if (event.kind === 'task') navigate(`/dossiers/${event.dossierId}/taches`);
          else if (event.kind === 'appointment' && event.appointmentId) {
            navigate(`/dossiers/${event.dossierId}/agenda?appointment=${event.appointmentId}`);
          } else navigate(event.href || `/dossiers/${event.dossierId}/agenda`);
        }}
      />
      {slot ? (
        <CalendarQuickCreate
          key={`${slot.day}-${slot.time}-${slot.x}-${slot.y}`}
          slot={slot}
          onClose={() => setSlot(null)}
          onMore={(kind) => {
            setSlot(null);
            // Une audience se crée depuis son dossier : la fiche complète y est.
            if (kind === 'hearing') toast.message('Ouvrez le dossier, onglet Agenda, pour la fiche complète d’une audience.');
            else setMore(kind);
          }}
        />
      ) : null}
    </ListPageLayout>
  );
}
